import type { ApiFileUploadType } from '#/api/types/ApiFileUploadType';
import type { ApiS3FileId } from '#/api/types/ApiTypedId';
import { LargeFilePicker } from '#/components/LargeFilePicker/LargeFilePicker';
import { SmallFilePicker } from '#/components/SmallFilePicker/SmallFilePicker';
import { useS3FileUpload } from '#/hooks/useS3FileUpload';

import { Field } from './Field/Field';
import { useFormContext } from './FormContext';

type Props = {
  'aria-label'?: string;
  qaId?: string;
  accept?: string;
  description?: string;
  disabled?: boolean;
  downloadAttachment: boolean;
  extraAnalyticsData?: Record<string, unknown>;
  fileType: ApiFileUploadType;
  maxSizeInMB?: number;
  hideRemove?: boolean;
  label?: string;
  message?: string;
  header?: string;
  name: string;
  onChange?: (fileId: ApiS3FileId | null) => void;
  showDate?: boolean;
  required?: boolean;
  large?: boolean;
};

export function FilePickerField({
  qaId,
  description,
  downloadAttachment,
  extraAnalyticsData,
  fileType,
  maxSizeInMB = 10,
  hideRemove,
  label,
  header,
  name,
  large,
  onChange,
  ...props
}: Props) {
  const { getValue, updateValue, handleError } = useFormContext();

  const fileId = (getValue(name) as ApiS3FileId | null | undefined) ?? null;
  const disabled = Boolean(props.disabled);

  const { fileUploadStateAndActions } = useS3FileUpload({
    fileId,
    fileType,
    onChange: (newFileId) => {
      if (onChange) {
        onChange(newFileId);
      } else {
        updateValue(name, newFileId);
      }
    },
    onError: handleError,
    canRemove: !hideRemove,
    maxSizeInMB,
    extraAnalyticsData: extraAnalyticsData ?? null,
  });

  return (
    <Field
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      name={name}
      label={label}
      description={description}
      inputProps={{
        ...props,
        loading: fileUploadStateAndActions.loading,
        fileMeta: fileUploadStateAndActions.fileMeta,
      }}
      renderInput={({ qaId: autoQaId }) =>
        large ? (
          <LargeFilePicker
            downloadAttachment={downloadAttachment}
            qaId={qaId ?? autoQaId}
            name={name}
            header={header ?? null}
            disabled={disabled}
            accept={props.accept ?? null}
            customMessage={props.message ?? null}
            ariaLabel={props['aria-label'] ?? null}
            showDate={Boolean(props.showDate)}
            required={Boolean(props.required)}
            fileUploadStateAndActions={fileUploadStateAndActions}
          />
        ) : (
          <SmallFilePicker
            downloadAttachment={downloadAttachment}
            qaId={qaId ?? autoQaId}
            name={name}
            disabled={disabled}
            accept={props.accept ?? null}
            customMessage={props.message ?? null}
            ariaLabel={props['aria-label'] ?? null}
            showDate={Boolean(props.showDate)}
            required={Boolean(props.required)}
            fileUploadStateAndActions={fileUploadStateAndActions}
            maxSizeInMB={maxSizeInMB}
          />
        )
      }
    />
  );
}
